import { IAdminUser } from '@/types/response';

/** 管理画面ロールの表示名。アプリ用の 'User' は出さない */
export const ADMIN_ROLE_LABELS: Record<
  string,
  { label: string; color: string }
> = {
  Admin: { label: '管理者', color: 'blue' },
  DashboardViewer: { label: '閲覧専用', color: 'green' },
};

/** 権限の表示名。アプリ用の accessApp は出さない */
export const ADMIN_PERMISSION_LABELS: Record<string, string> = {
  accessAdmin: '管理画面すべて',
  accessDashboard: 'ダッシュボード',
};

export type AdminUserState = 'active' | 'suspended' | 'deleted';

/** 削除済み → 停止中 → 有効 の順に判定する */
export const adminUserState = (
  user: Pick<IAdminUser, 'status' | 'deletedAt'>,
): AdminUserState => {
  if (user.deletedAt) return 'deleted';
  if (user.status === 'deaActive') return 'suspended';

  return 'active';
};

export const ADMIN_USER_STATE_LABELS: Record<
  AdminUserState,
  { label: string; color: string }
> = {
  active: { label: '有効', color: 'success' },
  suspended: { label: '停止中', color: 'warning' },
  deleted: { label: '削除済み', color: 'default' },
};

/** 「3日前」などのおおまかな経過。1時間単位でしか記録しないので1時間未満はまとめる */
export const formatElapsed = (iso: string, now: Date = new Date()): string => {
  const minutes = Math.floor((now.getTime() - new Date(iso).getTime()) / 60000);

  if (minutes < 60) return '1時間以内';
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)}時間前`;
  const days = Math.floor(minutes / (24 * 60));
  if (days < 31) return `${days}日前`;
  if (days < 365) return `${Math.floor(days / 30)}か月前`;

  return `${Math.floor(days / 365)}年以上前`;
};

/** 古い順。null（記録なし）はいちばん古い扱い（新しい順にすると最後に回る） */
export const compareDate = (a: string | null, b: string | null): number =>
  (a ? new Date(a).getTime() : 0) - (b ? new Date(b).getTime() : 0);

/** 画面から付け替えできるロール（サーバーの ADMIN_ASSIGNABLE_ROLES と揃える） */
export const ADMIN_ROLE_OPTIONS = ['Admin', 'DashboardViewer'].map((value) => ({
  value,
  label: ADMIN_ROLE_LABELS[value].label,
}));

/** 管理画面ロール（Admin / DashboardViewer）のうち持っているもの。アプリ用の User は除く */
export const adminRoleOf = (
  user: Pick<IAdminUser, 'roles'>,
): string | undefined =>
  user.roles.includes('Admin')
    ? 'Admin'
    : user.roles.find((name) => ADMIN_ROLE_LABELS[name]);

/** 管理者の役割（Admin）を持つかどうか */
export const isAdminRole = (roles: string[]): boolean =>
  roles.includes('Admin');

/** 有効な Admin の人数（サーバーの「最後の有効 Admin」判定と同じ条件） */
export const countActiveAdmins = (
  users: Pick<IAdminUser, 'status' | 'deletedAt' | 'roles'>[],
): number =>
  users.filter(
    (user) => adminUserState(user) === 'active' && isAdminRole(user.roles),
  ).length;

/**
 * 停止・権限変更ができない理由。null なら操作できる。
 * サーバー側でも同じ条件で拒否するので、ここは表示の先回りに使う。
 */
export const accessLockReason = (
  user: Pick<IAdminUser, 'id' | 'status' | 'deletedAt' | 'roles'>,
  ctx: { currentUserId?: number; activeAdminCount: number },
): string | null => {
  if (user.deletedAt) return '削除済みのユーザーは変更できません';
  if (user.id === ctx.currentUserId) {
    return '自分自身の停止・権限変更はできません';
  }
  const isLastActiveAdmin =
    adminUserState(user) === 'active' &&
    isAdminRole(user.roles) &&
    ctx.activeAdminCount <= 1;
  if (isLastActiveAdmin) {
    return '有効な管理者が1人のため変更できません';
  }

  return null;
};

/** サーバーのエラーコード（IResponseError.error）を表示文言にする */
const ACCESS_ERROR_LABELS: Record<string, string> = {
  ADMIN_SELF_CHANGE_NOT_ALLOWED: '自分自身の停止・権限変更はできません',
  LAST_ADMIN_REQUIRED: '有効な管理者が1人以上必要なため変更できません',
};

export const accessErrorLabel = (code?: string): string =>
  (code && ACCESS_ERROR_LABELS[code]) || '変更できませんでした';
